/**
 * Personaje que el usuario venía buscando cuando entró desde una landing.
 *
 * Problema que resuelve: las landings públicas mandan a la página de acceso y,
 * tras el login, `AuthScreen` navega a `/dashboard` sin más. Quien llegaba
 * buscando al Conejo de Pascua aterrizaba en una galería de 22 personajes y
 * tenía que volver a encontrarlo.
 *
 * Se guarda en cookie y no en un query param porque el login social sale de la
 * app y vuelve por `/auth/callback`: un param en la URL de la página de acceso
 * no sobrevive ese salto. Cookie de sesión corta, sin datos personales — solo un
 * id de personaje del catálogo, validado contra el propio catálogo tanto al
 * escribir como al leer.
 */

import { getCharacterById } from '@/lib/characters'
import { readCookie } from '@/lib/cookies'
import { CUSTOM_CHARACTER_ID, isCustomCharacterId } from '@/server/domain/custom-character'

const COOKIE_NAME = 'pn_pending_character'
/** Ventana suficiente para completar el login (incluido el OTP por email). */
const MAX_AGE_SECONDS = 30 * 60

/**
 * id de personaje → personKey canónico. El personaje propio no está en el
 * catálogo estático (lo crea cada cuenta): su id es un valor de dominio y su
 * `personKey` es él mismo. ÚNICO sitio donde vive esa regla.
 */
function personKeyFor(characterId: string): string | undefined {
  if (isCustomCharacterId(characterId)) return CUSTOM_CHARACTER_ID
  return getCharacterById(characterId)?.personKey
}

/** Escribe la intención. Ignora en silencio un id que no esté en el catálogo. */
export function rememberPendingCharacter(characterId: string | null | undefined): void {
  if (typeof document === 'undefined') return
  if (!characterId || personKeyFor(characterId) === undefined) return
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(characterId)}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`
}

/**
 * El id se escribe con `encodeURIComponent`, así que aquí sí hace falta
 * desescaparlo — y por tanto protegerlo: `decodeURIComponent` lanza `URIError`
 * con un escape mal formado (`%`), y una cookie corrupta no debe tumbar el
 * dashboard. Un valor ilegible se trata como "no había intención".
 */
function decodeCharacterId(raw: string): string | undefined {
  try {
    return decodeURIComponent(raw)
  } catch {
    return undefined
  }
}

/**
 * Devuelve la persona canónica pendiente y la borra: es de un solo uso, para
 * que recargar el dashboard más tarde no vuelva a resaltar el mismo personaje.
 */
export function consumePendingCharacter(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const raw = readCookie(COOKIE_NAME)
  clearPendingCharacter()
  if (!raw) return undefined
  const characterId = decodeCharacterId(raw)
  return characterId ? personKeyFor(characterId) : undefined
}

export function clearPendingCharacter(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}
